/**
 * ScreensaverOverlay — biome-name caption + transition fade for the
 * screensaver mode. Owns the fade-to-black because CameraManager.fadeToColor
 * is a logging-only stub; a CSS-animated black div is decoupled from the
 * camera substrate and just works.
 *
 * The ScreensaverSystem (in src/game/) drives this via the global handle
 * `globalThis.__screensaverOverlay` (mirrors the ErrorToast pattern — avoids an
 * ESM import cycle from a game-loop system into React).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SCREENSAVER_TUNABLES } from '../../../config/screensaver/ScreensaverConfig';
import { EventBus, EVENTS } from '../../../shared/EventBus';

export interface ScreensaverOverlayHandle {
  fadeToBlack(ms: number, onComplete: () => void): void;
  fadeFromBlack(ms: number, onComplete: () => void): void;
  showBiomeName(label: string): void;
}

declare global {
  // eslint-disable-next-line no-var
  var __screensaverOverlay: ScreensaverOverlayHandle | undefined;
}

export const ScreensaverOverlay: React.FC = () => {
  const [blackOpacity, setBlackOpacity] = useState(0);
  const [fadeMs, setFadeMs] = useState<number>(SCREENSAVER_TUNABLES.fadeMs);
  const [biomeName, setBiomeName] = useState<string | null>(null);
  const [nameVisible, setNameVisible] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const fadeToBlack = useCallback((ms: number, onComplete: () => void) => {
    setFadeMs(ms);
    setBlackOpacity(1);
    timers.current.push(setTimeout(onComplete, ms));
  }, []);

  const fadeFromBlack = useCallback((ms: number, onComplete: () => void) => {
    setFadeMs(ms);
    setBlackOpacity(0);
    timers.current.push(setTimeout(onComplete, ms));
  }, []);

  const showBiomeName = useCallback((label: string) => {
    const { fadeInS, holdS, fadeOutS } = SCREENSAVER_TUNABLES.overlay;
    setBiomeName(label);
    setNameVisible(true);
    timers.current.push(
      setTimeout(() => setNameVisible(false), (fadeInS + holdS) * 1000),
    );
    timers.current.push(
      setTimeout(() => setBiomeName(null), (fadeInS + holdS + fadeOutS) * 1000),
    );
  }, []);

  useEffect(() => {
    const handle: ScreensaverOverlayHandle = { fadeToBlack, fadeFromBlack, showBiomeName };
    globalThis.__screensaverOverlay = handle;
    return () => {
      clearTimers();
      if (globalThis.__screensaverOverlay === handle) globalThis.__screensaverOverlay = undefined;
    };
  }, [fadeToBlack, fadeFromBlack, showBiomeName, clearTimers]);

  // EventBus-driven caption (replaces the stale globalThis handle for the
  // caption path). The ScreensaverSystem emits SCREENSAVER_CAPTION via
  // emitSticky — this subscription receives it via replay-on-subscribe, so the
  // FIRST biome's caption (emitted during kernel boot, before this overlay
  // mounts) is correctly displayed. The `enabled` flag in the payload respects
  // the controller panel's overlay toggle.
  useEffect(() => {
    const bus = EventBus.getInstance();
    const unsub = bus.on(EVENTS.SCREENSAVER_CAPTION, (p: { label: string; enabled?: boolean }) => {
      if (p.enabled === false) return; // overlay toggled off via controller
      showBiomeName(p.label);
    });
    return unsub;
  }, [showBiomeName]);

  const nameFadeS = SCREENSAVER_TUNABLES.overlay.fadeInS;

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9000 }}>
      {/* transition fade */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: '#000',
          opacity: blackOpacity,
          transition: `opacity ${fadeMs}ms ease-in-out`,
        }}
      />
      {/* biome name caption */}
      {biomeName !== null && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: nameVisible ? 0.85 : 0,
            transition: `opacity ${nameFadeS}s ease-in-out`,
          }}
        >
          <span
            style={{
              color: '#fff',
              fontSize: '4rem',
              fontWeight: 300,
              letterSpacing: '0.12em',
              textShadow: '0 2px 24px rgba(0,0,0,0.8)',
            }}
          >
            {biomeName}
          </span>
        </div>
      )}
      {/* watermark */}
      <div
        style={{
          position: 'absolute',
          left: 16,
          bottom: 12,
          color: 'rgba(255,255,255,0.4)',
          fontSize: '0.9rem',
          letterSpacing: '0.18em',
        }}
      >
        H2000
      </div>
    </div>
  );
};
